import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, MessageCircle, Truck, Shield, CheckCircle, User, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useCart();
  const [settings, setSettings] = useState({ qrCodeUrl: '', whatsappNumber: '01167659164' });
  const [form, setForm] = useState({ nama: '', telefon: '', alamat: '', poskod: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}/api/settings`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(() => {});
  }, []);

  const item = cartItems[0];
  const totalPrice = item ? item.salePrice : 0;

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.nama.trim()) e.nama = 'Sila masukkan nama';
    if (!form.telefon.trim()) e.telefon = 'Sila masukkan no. telefon';
    if (!form.alamat.trim()) e.alamat = 'Sila masukkan alamat';
    if (!form.poskod.trim()) e.poskod = 'Sila masukkan poskod';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const sendToWhatsApp = () => {
    if (!item || !validate()) return;
    const message = `Assalamualaikum Boss Ijan!\n\n` +
      `Saya ingin membuat pesanan:\n\n` +
      `*Produk:* ${item.name}\n` +
      `*Harga:* RM${totalPrice.toLocaleString()}\n\n` +
      `📦 *MAKLUMAT PENGHANTARAN:*\n` +
      `Nama Penuh: ${form.nama}\n` +
      `No. Telefon: ${form.telefon}\n` +
      `Alamat Penuh: ${form.alamat}\n` +
      `Poskod & Negeri: ${form.poskod}\n\n` +
      `Saya akan lampirkan bukti pembayaran. Terima kasih!`;
    const whatsappUrl = `https://wa.me/6${settings.whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-600 mb-2">Tiada produk dipilih</p>
            <p className="text-sm text-gray-400 mb-4">Pilih produk untuk membeli</p>
            <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">
              Lihat Produk
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <button 
            onClick={() => { clearCart(); navigate('/'); }}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 text-sm"
          >
            <ArrowLeft className="mr-1" size={18} />
            Kembali
          </button>
          <h1 className="text-2xl font-bold text-gray-900 text-center">Checkout</h1>
        </div>

        {/* Shipping Details Form */}
        <Card className="mb-4 border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-amber-700" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">Maklumat Penghantaran</h3>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600">Nama Penuh</Label>
                <Input
                  value={form.nama}
                  onChange={e => set('nama', e.target.value)}
                  placeholder="Contoh: Ahmad bin Ali"
                  className={`mt-1 ${errors.nama ? 'border-red-400' : ''}`}
                  data-testid="checkout-nama"
                />
                {errors.nama && <p className="text-xs text-red-500 mt-1">{errors.nama}</p>}
              </div>
              <div>
                <Label className="text-xs text-gray-600">No. Telefon</Label>
                <Input
                  value={form.telefon}
                  onChange={e => set('telefon', e.target.value)}
                  placeholder="Contoh: 0123456789"
                  type="tel"
                  className={`mt-1 ${errors.telefon ? 'border-red-400' : ''}`}
                  data-testid="checkout-telefon"
                />
                {errors.telefon && <p className="text-xs text-red-500 mt-1">{errors.telefon}</p>}
              </div>
              <div>
                <Label className="text-xs text-gray-600">Alamat Penuh</Label>
                <textarea
                  value={form.alamat}
                  onChange={e => set('alamat', e.target.value)}
                  placeholder="No. rumah, jalan, taman, bandar"
                  className={`mt-1 w-full h-20 rounded-md border px-3 py-2 text-sm resize-none ${errors.alamat ? 'border-red-400' : 'border-gray-200'}`}
                  data-testid="checkout-alamat"
                />
                {errors.alamat && <p className="text-xs text-red-500 mt-1">{errors.alamat}</p>}
              </div>
              <div>
                <Label className="text-xs text-gray-600">Poskod & Negeri</Label>
                <Input
                  value={form.poskod}
                  onChange={e => set('poskod', e.target.value)}
                  placeholder="Contoh: 81100 Johor"
                  className={`mt-1 ${errors.poskod ? 'border-red-400' : ''}`}
                  data-testid="checkout-poskod"
                />
                {errors.poskod && <p className="text-xs text-red-500 mt-1">{errors.poskod}</p>}
              </div>
            </div>

            {/* QR Code Payment */}
            {settings.qrCodeUrl && (
              <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4 text-center" data-testid="qr-code-section">
                <p className="text-sm font-semibold text-gray-800 mb-3">Imbas QR untuk pembayaran:</p>
                <img
                  src={settings.qrCodeUrl}
                  alt="QR Code Pembayaran"
                  className="mx-auto max-w-[220px] rounded-lg border border-gray-100"
                  data-testid="qr-code-image"
                />
              </div>
            )}

            {/* Instruction after form */}
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <p className="text-sm text-amber-900 font-medium">
                Setelah mengisi maklumat, tekan butang di bawah untuk hantar pesanan terus ke WhatsApp.
              </p>
            </div>

            {/* WhatsApp Button */}
            <Button
              onClick={sendToWhatsApp}
              className="mt-4 w-full bg-green-500 hover:bg-green-600 text-white py-6 text-base font-semibold rounded-xl shadow-lg"
              data-testid="whatsapp-order-btn"
            >
              <Phone className="mr-2" size={20} />
              Hantar Pesanan via WhatsApp
            </Button>
          </CardContent>
        </Card>

        {/* Product Summary */}
        <Card className="mb-4 border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex gap-4">
              <img 
                src={item.images?.[0] || item.image}
                alt={item.name}
                className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
              />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{item.name}</h3>
                <p className="text-2xl font-bold text-amber-700">RM{totalPrice.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Pesanan akan diproses selepas pengesahan. Penghantaran 3-7 hari bekerja.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
